<template>
  <draggable-panel
    :visible="visible"
    :title="title"
    :icon="icon"
    :width="950"
    :height="590"
    :tabs="tabs"
    :active-tab="activeTab"
    :initial-position="{ top: 60 }"
    :z-index="zIndex"
    :enable-expand="true"
    :is-expanded="isExpanded"
    :expanded-width="1460"
    @close="handleClose"
    @tab-change="handleTabChange"
    @panel-focus="handlePanelFocus"
    @expand-toggle="handleExpandToggle"
  >
    <!-- 内容区域 -->
    <div v-loading="loading" class="panel-content">
      <!-- 搜索表单 -->
      <div class="search-form">
        <el-form 
          :inline="true" 
          :model="searchForm" 
          class="inline-search-form" 
          size="small"
          label-width="80px"
        >
          <el-form-item label="桥梁" class="form-item-bridge">
            <el-select
              v-model="searchForm.bridgeId"
              placeholder="请选择桥梁"
              filterable
              clearable
              size="small"
              style="width: 180px;"
              :disabled="!hasAdminAccess"
            >
              <el-option
                v-for="bridge in bridgeOptions"
                :key="bridge.id"
                :label="bridge.name"
                :value="bridge.id"
              />
            </el-select>
          </el-form-item>
          
          <el-form-item label="时间范围" class="form-item-time">
            <el-date-picker
              v-model="searchForm.timeRange"
              type="datetimerange"
              range-separator="至"
              start-placeholder="开始时间"
              end-placeholder="结束时间"
              value-format="yyyy-MM-dd HH:mm:ss"
              :default-time="['00:00:00', '23:59:59']"
              :clearable="false"
              size="small"
              style="width: 350px;"
            />
          </el-form-item>
          
          
          <el-form-item class="form-item-buttons">
            <el-button 
              type="primary" 
              size="small" 
              @click="handleSearch" 
              icon="el-icon-search"
            >
              查询
            </el-button>
            <el-button 
              size="small" 
              @click="handleReset"
              icon="el-icon-refresh"
            >
              重置
            </el-button>
          </el-form-item>
        </el-form>
      </div>

      <!-- 统计概览 -->
      <div class="statistics-overview" style="display: none;">
        <div class="overview-card">
          <div class="card-header">
            <i class="el-icon-s-data card-icon"></i>
            <span class="card-title">总偏航次数</span>
          </div>
          <div class="card-value">{{ statisticsData.totalCount || 0 }}</div>
        </div>
        <div class="overview-card">
          <div class="card-header">
            <i class="el-icon-time card-icon"></i>
            <span class="card-title">查询时间段</span>
          </div>
          <div class="card-value-small">
            {{ formatTimeRange() }}
          </div>
        </div>
      </div>

      <!-- 图表区域 -->
      <div class="charts-section">
        <div class="chart-container">
          <div class="chart-header">
            <h3>{{ chartTitle }}</h3>
            <el-tooltip :content="getTimeSeriesTooltip()" placement="top">
              <i class="el-icon-info"></i>
            </el-tooltip>
          </div>
          <div class="chart-content">
            <div ref="timeSeriesChart" class="chart-echarts"></div>
          </div>
        </div>

        <div class="chart-container">
          <div class="chart-header">
            <h3>{{ vesselTypeChartTitle }}</h3>
            <el-tooltip :content="getVesselTypeTooltip()" placement="top">
              <i class="el-icon-info"></i>
            </el-tooltip>
          </div>
          <div class="chart-content">
            <div ref="vesselTypePieChart" class="chart-echarts"></div>
          </div>
        </div>
      </div>
    </div>
  </draggable-panel>
</template>

<script>
import { getYawDeviationStatistics, getTrafficFlowStatistics, getComprehensiveAlertStatistics } from '@/api/comprehensive-query'
import { getBridgeList, getAllBridges } from '@/api/bridge'
import { formatDate } from '@/utils/dateUtils'
import DraggablePanel from '@/components/common/DraggablePanel.vue'
import { hasAdminAccess } from '@/utils/permissionUtils'

// 导入 ECharts
import * as echarts from 'echarts'

export default {
  name: 'BridgeStatistics',
  components: {
    DraggablePanel
  },
  props: {
    visible: {
      type: Boolean,
      default: false
    },
    bridgeId: {
      type: [Number, String],
      default: null
    },
    initialActiveTab: {
      type: String,
      default: 'deviation'
    },
    zIndex: {
      type: Number,
      default: 1000
    }
  },
  computed: {
    currentStatisticsData() {
      if (this.activeTab === 'traffic') {
        return this.trafficStatisticsData
      } else if (this.activeTab === 'alert') {
        return this.alertStatisticsData
      } else {
        return this.statisticsData
      }
    },
    chartTitle() {
      if (this.activeTab === 'traffic') {
        return `流量趋势分析-按小时 (总通航次数：${this.trafficStatisticsData.totalCount || 0})`
      } else if (this.activeTab === 'alert') {
        return `预警趋势分析-按小时 (总预警次数：${this.alertStatisticsData.totalCount || 0})`
      } else {
        return `偏航趋势分析-按小时 (总偏航次数：${this.statisticsData.totalCount || 0})`
      }
    },
    currentVesselTypeData() {
      if (this.activeTab === 'alert') {
        return this.alertStatisticsData.levelData || []
      } else {
        return this.currentStatisticsData.vesselTypeData || []
      }
    },
    vesselTypeChartTitle() {
      if (this.activeTab === 'alert') {
        return '预警级别分布'
      } else {
        return '船舶类型分布'
      }
    }
  },
  data() {
    return {
      title: '桥梁统计分析',
      icon: 'el-icon-s-data',
      tabs: [
        { key: 'deviation', label: '偏航统计' },
        { key: 'traffic', label: '流量统计' },
        // { key: 'alert', label: '预警统计' }
      ],
      activeTab: 'deviation',
      isExpanded: false, // 展开/收起状态
      loading: false,
      searchForm: {
        bridgeId: null,
        vesselType: '',
        timeRange: this.getDefaultTimeRange()
      },
      statisticsData: {
        totalCount: 0,
        timeSeriesData: [],
        vesselTypeData: [],
        startTime: null,
        endTime: null
      },
      trafficStatisticsData: {
        totalCount: 0,
        timeSeriesData: [],
        vesselTypeData: [],
        startTime: null,
        endTime: null
      },
      alertStatisticsData: {
        totalCount: 0,
        timeSeriesData: [],
        levelData: [],
        typeData: [],
        statusData: [],
        startTime: null,
        endTime: null
      },
      bridgeOptions: [],
      vesselTypeOptions: [
        { label: '货船', value: 'cargo' },
        { label: '客船', value: 'passenger' },
        { label: '油船', value: 'tanker' },
        { label: '渔船', value: 'fishing' },
        { label: '其他', value: 'other' }
      ],
      timeSeriesChartInstance: null,
      vesselTypePieChartInstance: null,
      // 添加权限控制相关数据
      hasAdminAccess: false
    }
  },
  watch: {
    visible(val) {
      if (val) {
        this.init()
      } else {
        this.destroyCharts()
      }
    },
    bridgeId(val) {
      if (val) {
        this.searchForm.bridgeId = Number(val)
        if (this.visible) {
          this.loadCurrentTabStatistics()
        }
      }
    },
    initialActiveTab: {
      handler(val) {
        if (val) {
          this.activeTab = val
        }
      },
      immediate: true
    }
  },
  beforeDestroy() {
    this.destroyCharts()
  },
  methods: {
    async init() {
      // 初始化权限检查
      this.initPermissionCheck()
      
      await this.loadBridgeOptions()
      if (this.bridgeId) {
        this.searchForm.bridgeId = Number(this.bridgeId)
      }
      await this.loadCurrentTabStatistics()
      this.$nextTick(() => {
        this.initCharts()
      })
    },

    /**
     * 初始化权限检查
     */
    initPermissionCheck() {
      this.hasAdminAccess = hasAdminAccess()
      console.log('桥梁统计分析组件权限检查结果:', { hasAdminAccess: this.hasAdminAccess })
    },

    async loadBridgeOptions() {
      try {
        const response = await getAllBridges()
        if (response && response.code === 200 && response.data) {
          // 倒序排列桥梁列表
          this.bridgeOptions = (response.data || [])
            .reverse()
            .map(bridge => ({
              id: bridge.id,
              name: bridge.bridgeName || bridge.name || `桥梁${bridge.id}`,
              bridgeCode: bridge.bridgeCode
            }))
          
          // 如果没有管理员权限，只显示当前桥梁
          if (!this.hasAdminAccess && this.bridgeId) {
            this.bridgeOptions = this.bridgeOptions.filter(bridge => bridge.id === Number(this.bridgeId))
            console.log('【BridgeDeviationStatistics】非管理员用户，桥梁选项已过滤为当前桥梁:', this.bridgeOptions)
          }
          
          console.log('【BridgeDeviationStatistics】加载桥梁列表成功:', this.bridgeOptions.length, '条记录')
        }
      } catch (error) {
        console.error('【BridgeDeviationStatistics】加载桥梁列表失败:', error)
      }
    },

    getDefaultTimeRange() {
      const now = new Date()
      const today = new Date(now.getFullYear(), now.getMonth(), now.getDate())
      
      const formatDateTime = (date) => {
        const year = date.getFullYear()
        const month = String(date.getMonth() + 1).padStart(2, '0')
        const day = String(date.getDate()).padStart(2, '0')
        const hours = String(date.getHours()).padStart(2, '0')
        const minutes = String(date.getMinutes()).padStart(2, '0')
        const seconds = String(date.getSeconds()).padStart(2, '0')
        return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`
      }
      
      const startTime = formatDateTime(today) // 今天00:00:00
      const endTime = formatDateTime(now)     // 当前时间
      
      return [startTime, endTime]
    },

    async loadCurrentTabStatistics() {
      if (this.activeTab === 'deviation') {
        await this.loadDeviationStatistics()
      } else if (this.activeTab === 'traffic') {
        await this.loadTrafficStatistics()
      } else if (this.activeTab === 'alert') {
        await this.loadAlertStatistics()
      }
    },

    async loadDeviationStatistics() {
      if (!this.searchForm.timeRange || this.searchForm.timeRange.length !== 2) {
        this.$message.warning('请选择时间范围')
        return
      }

      this.loading = true
      try {
        const params = {
          startTime: this.searchForm.timeRange[0],
          endTime: this.searchForm.timeRange[1],
          timeGranularity: 'hour', // 按小时统计
          groupBy: 'hour'
        }

        if (this.searchForm.bridgeId) {
          params.bridgeId = Number(this.searchForm.bridgeId)
        }
        if (this.searchForm.vesselType) {
          params.vesselType = this.searchForm.vesselType
        }

        const response = await getYawDeviationStatistics(params)
        
        if (response && response.code === 200 && response.data) {
          this.statisticsData = {
            totalCount: response.data.totalCount || 0,
            timeSeriesData: response.data.timeSeriesData || [],
            vesselTypeData: response.data.vesselTypeData || [],
            startTime: response.data.startTime,
            endTime: response.data.endTime
          }
          
          this.$nextTick(() => {
            this.updateCharts()
          })
        } else {
          this.$message.error(response?.message || '查询失败')
          this.statisticsData = {
            totalCount: 0,
            timeSeriesData: [],
            vesselTypeData: [],
            startTime: null,
            endTime: null
          }
        }
      } catch (error) {
        console.error('查询桥梁偏航统计失败:', error)
        this.$message.error('查询失败，请稍后重试')
        this.statisticsData = {
          totalCount: 0,
          timeSeriesData: [],
          vesselTypeData: [],
          startTime: null,
          endTime: null
        }
      } finally {
        this.loading = false
      }
    },

    async loadTrafficStatistics() {
      if (!this.searchForm.timeRange || this.searchForm.timeRange.length !== 2) {
        this.$message.warning('请选择时间范围')
        return
      }

      this.loading = true
      try {
        const params = {
          startTime: this.searchForm.timeRange[0],
          endTime: this.searchForm.timeRange[1],
          timeGranularity: 'hour', // 按小时统计
          groupBy: 'hour'
        }

        if (this.searchForm.bridgeId) {
          params.bridgeId = Number(this.searchForm.bridgeId)
        }
        if (this.searchForm.vesselType) {
          params.vesselType = this.searchForm.vesselType
        }

        const response = await getTrafficFlowStatistics(params)
        
        if (response && response.code === 200 && response.data) {
          this.trafficStatisticsData = {
            totalCount: response.data.totalCount || 0,
            timeSeriesData: response.data.timeSeriesData || [],
            vesselTypeData: response.data.vesselTypeData || [],
            startTime: response.data.startTime,
            endTime: response.data.endTime
          }
          
          this.$nextTick(() => {
            this.updateCharts()
          })
        } else {
          this.$message.error(response?.message || '查询失败')
          this.trafficStatisticsData = {
            totalCount: 0,
            timeSeriesData: [],
            vesselTypeData: [],
            startTime: null,
            endTime: null
          }
        }
      } catch (error) {
        console.error('查询桥梁流量统计失败:', error)
        this.$message.error('查询失败，请稍后重试')
        this.trafficStatisticsData = {
          totalCount: 0,
          timeSeriesData: [],
          vesselTypeData: [],
          startTime: null,
          endTime: null
        }
      } finally {
        this.loading = false
      }
    },

    async loadAlertStatistics() {
      if (!this.searchForm.timeRange || this.searchForm.timeRange.length !== 2) {
        this.$message.warning('请选择时间范围')
        return
      }

      this.loading = true
      try {
        const params = {
          startTime: this.searchForm.timeRange[0],
          endTime: this.searchForm.timeRange[1],
          timeGranularity: 'hour', // 按小时统计
          groupBy: 'hour'
        }

        if (this.searchForm.bridgeId) {
          params.bridgeId = Number(this.searchForm.bridgeId)
        }
        if (this.searchForm.vesselType) {
          params.vesselType = this.searchForm.vesselType
        }

        const response = await getComprehensiveAlertStatistics(params)
        
        if (response && response.code === 200 && response.data) {
          this.alertStatisticsData = {
            totalCount: response.data.totalCount || 0,
            timeSeriesData: response.data.timeSeriesData || [],
            levelData: response.data.levelData || [],
            typeData: response.data.typeData || [],
            statusData: response.data.statusData || [],
            startTime: response.data.startTime,
            endTime: response.data.endTime
          }
          
          this.$nextTick(() => {
            this.updateCharts()
          })
        } else {
          this.$message.error(response?.message || '查询失败')
          this.alertStatisticsData = {
            totalCount: 0,
            timeSeriesData: [],
            levelData: [],
            typeData: [],
            statusData: [],
            startTime: null,
            endTime: null
          }
        }
      } catch (error) {
        console.error('查询桥梁预警统计失败:', error)
        this.$message.error('查询失败，请稍后重试')
        this.alertStatisticsData = {
          totalCount: 0,
          timeSeriesData: [],
          levelData: [],
          typeData: [],
          statusData: [],
          startTime: null,
          endTime: null
        }
      } finally {
        this.loading = false
      }
    },

    handleSearch() {
      this.loadCurrentTabStatistics()
    },

    handleReset() {
      this.searchForm = {
        bridgeId: this.bridgeId ? Number(this.bridgeId) : null,
        vesselType: '',
        timeRange: this.getDefaultTimeRange()
      }
      this.loadCurrentTabStatistics()
    },

    handleClose() {
      this.$emit('update:visible', false)
    },

    handleTabChange(tabKey) {
      this.activeTab = tabKey
      // 切换标签页时加载对应的数据
      this.loadCurrentTabStatistics()
    },
    
    handlePanelFocus() {
      this.$emit('panel-focus')
    },
    
    /**
     * 处理展开/收起切换
     */
    handleExpandToggle(expanded) {
      this.isExpanded = expanded
    },

    formatTimeRange() {
      if (!this.searchForm.timeRange || this.searchForm.timeRange.length !== 2) {
        return '未选择'
      }
      const start = this.searchForm.timeRange[0].split(' ')[0]
      const end = this.searchForm.timeRange[1].split(' ')[0]
      return start === end ? start : `${start} 至 ${end}`
    },

    getTimeSeriesTooltip() {
      if (this.activeTab === 'traffic') {
        return '显示指定时间段内每小时的通航次数变化趋势'
      } else if (this.activeTab === 'alert') {
        return '显示指定时间段内每小时的预警次数变化趋势'
      } else {
        return '显示指定时间段内每小时的偏航次数变化趋势'
      }
    },

    getVesselTypeTooltip() {
      if (this.activeTab === 'traffic') {
        return '按船舶类型统计通航事件的分布情况'
      } else if (this.activeTab === 'alert') {
        return '按预警级别统计预警事件的分布情况'
      } else {
        return '按船舶类型统计偏航事件的分布情况'
      }
    },

    async initCharts() {
      try {
        // 初始化时间序列图表
        if (this.$refs.timeSeriesChart) {
          this.timeSeriesChartInstance = echarts.init(this.$refs.timeSeriesChart)
          this.updateTimeSeriesChart()
        }

        // 初始化饼图
        if (this.$refs.vesselTypePieChart) {
          this.vesselTypePieChartInstance = echarts.init(this.$refs.vesselTypePieChart)
          this.updateVesselTypePieChart()
        }

        // 监听窗口大小变化
        window.addEventListener('resize', this.handleChartResize)
      } catch (error) {
        console.error('初始化图表失败:', error)
      }
    },

    updateCharts() {
      this.updateTimeSeriesChart()
      this.updateVesselTypePieChart()
    },

    updateTimeSeriesChart() {
      if (!this.timeSeriesChartInstance) return

      const timeData = this.currentStatisticsData.timeSeriesData || []
      const xAxisData = timeData.map(item => {
        if (item.timeLabel) {
          return item.timeLabel
        }
        // 如果没有 timeLabel，从 time 字段提取小时
        const date = new Date(item.time)
        return `${String(date.getHours()).padStart(2, '0')}:00`
      })
      const seriesData = timeData.map(item => item.value || 0)

      const option = {
        tooltip: {
          trigger: 'axis',
          axisPointer: {
            type: 'cross'
          },
          formatter: (params) => {
            if (params && params.length > 0) {
              const data = params[0]
              let label = '偏航次数'
              if (this.activeTab === 'traffic') {
                label = '通航次数'
              } else if (this.activeTab === 'alert') {
                label = '预警次数'
              }
              return `时间: ${data.axisValue}<br/>${label}: ${data.value}`
            }
            return ''
          }
        },
        legend: {
          top: 30,
          data: [this.activeTab === 'traffic' ? '通航次数' : (this.activeTab === 'alert' ? '预警次数' : '偏航次数')]
        },
        grid: {
          left: '3%',
          right: '4%',
          bottom: '3%',
          containLabel: true
        },
        xAxis: {
          type: 'category',
          boundaryGap: false,
          data: xAxisData,
          axisLabel: {
            rotate: timeData.length > 12 ? 45 : 0
          }
        },
        yAxis: {
          type: 'value',
          name: this.activeTab === 'traffic' ? '通航次数' : (this.activeTab === 'alert' ? '预警次数' : '偏航次数'),
          nameTextStyle: {
            color: '#666'
          }
        },
        series: [
          {
            name: this.activeTab === 'traffic' ? '通航次数' : (this.activeTab === 'alert' ? '预警次数' : '偏航次数'),
            type: 'line',
            data: seriesData,
            smooth: true,
            lineStyle: {
              color: '#409EFF',
              width: 3
            },
            itemStyle: {
              color: '#409EFF'
            },
            areaStyle: {
              color: {
                type: 'linear',
                x: 0,
                y: 0,
                x2: 0,
                y2: 1,
                colorStops: [
                  { offset: 0, color: 'rgba(64, 158, 255, 0.3)' },
                  { offset: 1, color: 'rgba(64, 158, 255, 0.1)' }
                ]
              }
            }
          }
        ]
      }

      this.timeSeriesChartInstance.setOption(option, true)
    },

    updateVesselTypePieChart() {
      if (!this.vesselTypePieChartInstance) return

      let vesselData = []
      if (this.activeTab === 'alert') {
        // 预警统计使用级别数据
        vesselData = this.alertStatisticsData.levelData || []
      } else {
        // 偏航和流量统计使用船舶类型数据
        vesselData = this.currentStatisticsData.vesselTypeData || []
      }

      const pieData = vesselData.map(item => ({
        name: item.levelName || item.vesselTypeName || item.levelCode || item.vesselTypeCode || '未知',
        value: item.count || 0,
        percentage: item.percentage || 0
      }))

      const option = {
        tooltip: {
          trigger: 'item',
          formatter: (params) => {
            let label = '偏航次数'
            if (this.activeTab === 'traffic') {
              label = '通航次数'
            } else if (this.activeTab === 'alert') {
              label = '预警次数'
            }
            return `${params.name}<br/>
                   ${label}: ${params.value}<br/>
                   占比: ${params.percent}%`
          }
        },
        legend: {
          orient: 'vertical',
          left: 'left',
          top: 'middle',
          formatter: function(name) {
            const item = pieData.find(d => d.name === name)
            return item ? `${name} (${item.value})` : name
          }
        },
        series: [
          {
            name: '船舶类型',
            type: 'pie',
            radius: ['40%', '70%'],
            center: ['60%', '50%'],
            data: pieData,
            emphasis: {
              itemStyle: {
                shadowBlur: 10,
                shadowOffsetX: 0,
                shadowColor: 'rgba(0, 0, 0, 0.5)'
              }
            },
            label: {
              show: true,
              formatter: '{b}: {c}\n({d}%)'
            },
            labelLine: {
              show: true
            }
          }
        ]
      }

      this.vesselTypePieChartInstance.setOption(option, true)
    },

    handleChartResize() {
      if (this.timeSeriesChartInstance) {
        this.timeSeriesChartInstance.resize()
      }
      if (this.vesselTypePieChartInstance) {
        this.vesselTypePieChartInstance.resize()
      }
    },

    destroyCharts() {
      if (this.timeSeriesChartInstance) {
        this.timeSeriesChartInstance.dispose()
        this.timeSeriesChartInstance = null
      }
      if (this.vesselTypePieChartInstance) {
        this.vesselTypePieChartInstance.dispose()
        this.vesselTypePieChartInstance = null
      }
      window.removeEventListener('resize', this.handleChartResize)
    }
  }
}
</script>

<style scoped>
/* 面板内容样式 */
.panel-content {
  padding: 0;
  max-height: 75vh;
  overflow-y: auto;
}

/* 搜索表单样式 */
.search-form {
  margin: 12px 16px;
  padding: 12px 16px;
  background: #f8f9fa;
  border-radius: 6px;
  border: 1px solid #e9ecef;
}

/* 内联搜索表单样式 */
.inline-search-form {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 16px;
}

.inline-search-form .el-form-item {
  margin-bottom: 0;
  margin-right: 0;
  display: flex;
  align-items: center;
  white-space: nowrap;
}

.inline-search-form .el-form-item__label {
  margin-right: 8px;
  padding-right: 0;
  color: #606266;
  font-weight: 500;
  font-size: 13px;
  line-height: 1;
}

.inline-search-form .el-form-item__content {
  margin-left: 0 !important;
  line-height: 1;
}

/* 表单项特定样式 */
.form-item-bridge,
.form-item-vessel-type {
  min-width: auto;
}

.form-item-bridge {
  position: relative;
}

.permission-info-icon {
  margin-left: 8px;
  color: #909399;
  cursor: help;
  font-size: 14px;
}

.form-item-time {
  min-width: auto;
}

.form-item-buttons {
  margin-left: auto;
}

.form-item-buttons .el-button {
  margin-left: 8px;
}

.form-item-buttons .el-button:first-child {
  margin-left: 0;
}

/* 统计概览样式 */
.statistics-overview {
  display: flex;
  gap: 16px;
  margin: 16px;
  margin-bottom: 20px;
}

.overview-card {
  flex: 1;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  padding: 20px;
  border-radius: 8px;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
}

.card-header {
  display: flex;
  align-items: center;
  margin-bottom: 12px;
}

.card-icon {
  font-size: 18px;
  margin-right: 8px;
}

.card-title {
  font-size: 14px;
  opacity: 0.9;
}

.card-value {
  font-size: 28px;
  font-weight: bold;
  line-height: 1;
}

.card-value-small {
  font-size: 16px;
  font-weight: 500;
  line-height: 1.2;
}

/* 图表区域样式 */
.charts-section {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 20px;
  margin: 16px;
}

.chart-container {
  background: white;
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  overflow: hidden;
}

.chart-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px 20px;
  background: #fafafa;
  border-bottom: 1px solid #e8e8e8;
}

.chart-header h3 {
  margin: 0;
  font-size: 16px;
  font-weight: 600;
  color: #303133;
}

.chart-header .el-icon-info {
  color: #909399;
  cursor: help;
}

.chart-content {
  padding: 16px;
}

.chart-echarts {
  width: 100%;
  height: 300px;
  min-height: 300px;
}

/* 响应式适配 */
@media (max-width: 1400px) {
  .charts-section {
    grid-template-columns: 1fr;
  }
  
  .chart-echarts {
    height: 280px;
  }
}

@media (max-width: 1200px) {
  .inline-search-form {
    flex-wrap: wrap;
    gap: 12px;
  }
  
  .form-item-buttons {
    margin-left: 0;
    width: 100%;
    margin-top: 8px;
  }
  
  .form-item-time .el-date-editor {
    width: 300px !important;
  }
  
  .statistics-overview {
    flex-direction: column;
  }
}
</style>

